import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  AgencyUserFormField,
  AgencyUserFormFieldErrors,
  AgencyUserFormFields,
  AgencyUserFormMode,
} from '@/modules/agencies/types/agencyUserForm'

type AgencyUsersUiState = {
  mode: AgencyUserFormMode
  agencyId: string | null
  editingUserId: string | null
  fields: AgencyUserFormFields
  fieldErrors: AgencyUserFormFieldErrors
  submitError: string | null
  isSubmitting: boolean
}

const emptyFields: AgencyUserFormFields = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  role: '',
  status: '',
}

const initialState: AgencyUsersUiState = {
  mode: 'closed',
  agencyId: null,
  editingUserId: null,
  fields: emptyFields,
  fieldErrors: {},
  submitError: null,
  isSubmitting: false,
}

function resetForm(state: AgencyUsersUiState) {
  state.fields = emptyFields
  state.fieldErrors = {}
  state.submitError = null
  state.isSubmitting = false
}

const agencyUsersUiSlice = createSlice({
  name: 'agencyUsersUi',
  initialState,
  reducers: {
    createAgencyUserOpened(state, action: PayloadAction<{ agencyId: string }>) {
      state.mode = 'create'
      state.agencyId = action.payload.agencyId
      state.editingUserId = null
      resetForm(state)
      state.fields.role = 'admin'
      state.fields.status = 'active'
    },
    editAgencyUserOpened(
      state,
      action: PayloadAction<{
        agencyId: string
        userId: string
        firstName: string
        lastName: string
        email: string
        role: string
        status: string
      }>,
    ) {
      state.mode = 'edit'
      state.agencyId = action.payload.agencyId
      state.editingUserId = action.payload.userId
      state.fields = {
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        email: action.payload.email,
        password: '',
        role: action.payload.role,
        status: action.payload.status,
      }
      state.fieldErrors = {}
      state.submitError = null
      state.isSubmitting = false
    },
    agencyUserFormClosed(state) {
      state.mode = 'closed'
      state.agencyId = null
      state.editingUserId = null
      resetForm(state)
    },
    agencyUserFormFieldChanged(
      state,
      action: PayloadAction<{ field: AgencyUserFormField; value: string }>,
    ) {
      const { field, value } = action.payload
      state.fields[field] = value
      state.submitError = null
      if (state.fieldErrors[field]) {
        delete state.fieldErrors[field]
      }
    },
    agencyUserFormValidationFailed(
      state,
      action: PayloadAction<AgencyUserFormFieldErrors>,
    ) {
      state.fieldErrors = action.payload
      state.isSubmitting = false
    },
    agencyUserFormSubmitted(state) {
      state.isSubmitting = true
      state.submitError = null
      state.fieldErrors = {}
    },
    agencyUserFormSucceeded(state) {
      state.mode = 'closed'
      state.agencyId = null
      state.editingUserId = null
      resetForm(state)
    },
    agencyUserFormFailed(state, action: PayloadAction<string>) {
      state.isSubmitting = false
      state.submitError = action.payload
    },
  },
})

export const {
  createAgencyUserOpened,
  editAgencyUserOpened,
  agencyUserFormClosed,
  agencyUserFormFieldChanged,
  agencyUserFormValidationFailed,
  agencyUserFormSubmitted,
  agencyUserFormSucceeded,
  agencyUserFormFailed,
} = agencyUsersUiSlice.actions

export const agencyUsersUiReducer = agencyUsersUiSlice.reducer
